import { AlertTriangle, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Tile, TileState, Empty, Chip } from "../Tile";
import { ago } from "../../../lib/dash";
import { InviteDialog, ROLE_LABEL } from "./InviteDialog";

export const ROLE_TONE = { owner: "blue", admin: "blue", partner: "green" };
export const STATUS_TONE = { disabled: "red", invited: "amber", active: "green" };
const ROLES = ["owner", "admin", "staff", "partner", "customer"];
const STATUSES = [["", "Any status"], ["active", "Active"], ["invited", "Invited"], ["disabled", "Disabled"]];

const FilterChip = ({ testid, on, onClick, children }) => (
  <button type="button" data-testid={testid} aria-pressed={on} onClick={onClick}
    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums transition-colors ${on ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground"}`}>
    {children}
  </button>
);

const RoleFilter = ({ counts = {}, role, onRole }) => (
  <div data-testid="accounts-role-filter" className="mt-2 flex flex-wrap items-center gap-1.5">
    <FilterChip testid="accounts-filter-all" on={!role} onClick={() => onRole("")}>
      All {Object.values(counts).reduce((a, b) => a + b, 0)}
    </FilterChip>
    {ROLES.map((r) => (
      <FilterChip key={r} testid={`accounts-filter-${r}`} on={role === r} onClick={() => onRole(r)}>
        {ROLE_LABEL[r]} {counts[r] ?? 0}
      </FilterChip>
    ))}
  </div>
);

const COLS = "grid-cols-[minmax(0,1fr)_74px_74px_70px]";

const AccountRow = ({ a, picked, onPick }) => (
  <li>
    <button type="button" data-testid={`account-row-${a.id}`} aria-pressed={picked} onClick={() => onPick(a.id)}
      className={`grid w-full ${COLS} items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-muted/70 ${picked ? "bg-primary/15" : ""}`}>
      <span className="min-w-0">
        <span className="block truncate font-semibold">{a.name || a.email}</span>
        <span className="block truncate text-[11px] text-muted-foreground">{a.name ? a.email : `${ROLE_LABEL[a.role]} account`}</span>
      </span>
      <span><Chip tone={ROLE_TONE[a.role]}>{ROLE_LABEL[a.role]}</Chip></span>
      <span><Chip testid={`account-row-${a.id}-status`} tone={STATUS_TONE[a.status]}>{a.status}</Chip></span>
      <span className="text-right text-[11px] tabular-nums text-muted-foreground">{a.status === "invited" ? "not yet" : ago(a.last_login_at)}</span>
    </button>
  </li>
);

const PageButton = ({ testid, disabled, onClick, icon: Icon, children }) => (
  <button type="button" data-testid={testid} disabled={disabled} onClick={onClick}
    className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40">
    <Icon className="h-3 w-3" /> {children}
  </button>
);

const Pager = ({ d, onPage }) => {
  const to = Math.min(d.total, (d.page + 1) * d.page_size);
  return (
    <div className="mt-2 flex items-center gap-2 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
      <span data-testid="accounts-range">{d.total ? d.page * d.page_size + 1 : 0}–{to} of {d.total}</span>
      <span className="ml-auto flex gap-1.5">
        <PageButton testid="accounts-prev" disabled={d.page === 0} onClick={() => onPage(d.page - 1)} icon={ChevronLeft}>Previous</PageButton>
        <PageButton testid="accounts-next" disabled={to >= d.total} onClick={() => onPage(d.page + 1)} icon={ChevronRight}>Next</PageButton>
      </span>
    </div>
  );
};

const Toolbar = ({ query, set }) => (
  <div className="flex items-center gap-2">
    <label className="relative min-w-0 flex-1">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input data-testid="accounts-search" value={query.q} placeholder="Search name or email" aria-label="Search accounts"
        onChange={(e) => set({ q: e.target.value })}
        className="h-8 w-full rounded-full border border-input bg-background pl-8 pr-3 text-[13px] outline-none transition-colors focus:border-primary" />
    </label>
    <select data-testid="accounts-status-filter" value={query.status} aria-label="Status" onChange={(e) => set({ status: e.target.value })}
      className="h-8 rounded-full border border-input bg-background px-2.5 text-[12px]">
      {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

export const AccountList = ({ list, query, onQuery, picked, onPick, actorRole, onInvited, adminEmailsSet }) => {
  const set = (patch) => onQuery({ ...query, page: 0, ...patch });
  const d = list.data;
  const manager = ["admin", "owner"].includes(actorRole);
  return (
    <Tile id="accounts-list" index={0} kicker="Accounts · every change is in the admin audit" title="Who can sign in"
      action={manager ? <InviteDialog actorRole={actorRole} onDone={onInvited} /> : <Chip testid="accounts-view-only">View only</Chip>}>
      <Toolbar query={query} set={set} />
      <RoleFilter counts={d?.counts} role={query.role} onRole={(role) => set({ role })} />
      {adminEmailsSet && (
        <p data-testid="accounts-admin-emails-warning" className="mt-2 flex items-start gap-1.5 rounded-lg bg-amber-100 px-2.5 py-1.5 text-[11px] text-amber-900">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
          ADMIN_EMAILS is still set. Open each admin it made and press "Manage role here", then remove it from Secrets.
        </p>
      )}
      <div className={`mt-2 grid ${COLS} gap-2 border-b border-border px-2 pb-1 font-mono2 text-[10px] uppercase tracking-wider text-muted-foreground`}>
        <span>Account</span><span>Role</span><span>Status</span><span className="text-right">Signed in</span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col pt-1">
        <TileState state={list}>
          {d?.items.length ? (
            <ul data-testid="accounts-list" className="dash-scroll min-h-0 flex-1 overflow-y-auto pr-1">
              {d.items.map((a) => <AccountRow key={a.id} a={a} picked={picked === a.id} onPick={onPick} />)}
            </ul>
          ) : <Empty testid="accounts-empty">No account matches.</Empty>}
        </TileState>
      </div>
      {d && <Pager d={d} onPage={(page) => onQuery({ ...query, page })} />}
    </Tile>
  );
};
